import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { USER_TIERS, ACCESS_LEVELS, canAccessContent, normalizeAccessLevel } from '../constants/accessLevels';

/**
 * Determine the user's access tier for a specific coach.
 *
 * Resolution order:
 *  1. Not logged in → VISITOR
 *  2. Is the coach  → OWNER
 *  3. Student record has hasPurchased → PAID
 *  4. purchases collection has a record → PAID
 *  5. Logged in but no purchases → AUTHENTICATED
 *
 * @param {string|null} userId  - Current user's Firebase UID (null if logged out)
 * @param {string}      coachId - Coach document ID
 * @param {string}      coachUserId - Coach's Firebase UID (from coach.userId)
 * @returns {Promise<string>} One of USER_TIERS values
 */
export async function resolveUserTier(userId, coachId, coachUserId) {
  if (!userId) return USER_TIERS.VISITOR;

  // Owner check
  if (userId === coachUserId) return USER_TIERS.OWNER;

  // Check student record for membership and purchase flags (fast path)
  try {
    const studentRef = doc(db, 'coaches', coachId, 'students', userId);
    const studentSnap = await getDoc(studentRef);
    if (studentSnap.exists()) {
      const data = studentSnap.data();
      // MEMBER first (higher tier)
      if (data.isMember === true && data.membershipCurrentPeriodEnd) {
        if (new Date(data.membershipCurrentPeriodEnd) > new Date()) {
          return USER_TIERS.MEMBER;
        }
      }
      // Then PAID
      if (data.hasPurchased === true) {
        return USER_TIERS.PAID;
      }
    }
  } catch (err) {
    console.error('Error checking student record:', err);
  }

  // Fallback: query purchases collection
  try {
    const purchasesRef = collection(db, 'purchases');
    const q = query(
      purchasesRef,
      where('buyerId', '==', userId),
      where('vendorId', '==', coachUserId),
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) return USER_TIERS.PAID;
  } catch (err) {
    console.error('Error checking purchases:', err);
  }

  return USER_TIERS.AUTHENTICATED;
}

/**
 * Check whether a user can access a specific library item.
 * Handles shop-linked overrides (items with linkedProductId).
 *
 * @param {string} userTier       - From resolveUserTier()
 * @param {object} item           - Library item/folder with accessLevel and optional linkedProductId
 * @param {string|null} userId    - Current user's Firebase UID
 * @param {string} coachUserId    - Coach's Firebase UID (vendorId in purchases)
 * @returns {Promise<boolean>}
 */
export async function canAccessItem(userTier, item, userId, coachUserId) {
  // Owner always has access
  if (userTier === USER_TIERS.OWNER) return true;

  // Admin-only: never accessible to non-owners
  if (normalizeAccessLevel(item?.accessLevel) === ACCESS_LEVELS.ADMIN) {
    return false;
  }

  // Student-gated: check if userId is in the allowedStudentIds list
  if (normalizeAccessLevel(item?.accessLevel) === ACCESS_LEVELS.STUDENTS) {
    const allowed = item.allowedStudentIds || [];
    return allowed.includes(userId);
  }

  // Shop-linked override: check if user purchased the specific product
  if (item?.linkedProductId && userId) {
    try {
      const purchasesRef = collection(db, 'purchases');
      const q = query(
        purchasesRef,
        where('buyerId', '==', userId),
        where('productId', '==', item.linkedProductId),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) return true;
    } catch (err) {
      console.error('Error checking product purchase:', err);
    }
  }

  // Standard tier-based check
  const normalized = normalizeAccessLevel(item?.accessLevel);
  return canAccessContent(userTier, normalized);
}
